import {createRngFromStr, edgesToStr, FeatureType, HexEdge, TerrainType} from "@hexcrawl/shared-utils";
import {loadTilesetJSON, TileData, TilesetData} from '@/map/tiles/TileSetData';

/**
 * Tile selection manager - handles weighted random selection with consistent seeding
 */
export class TileSelectionManager {
  public terrainTilesets: TilesetData[] = [];
  public featureTilests: TilesetData[] = [];
  private selections: Map<string, number> = new Map();

  async loadTileSets(
    terrainUrls: string[],
    featureUrls: string[]
  ): Promise<TilesetData[]> {

    let nextId = 1;
    for (const url of terrainUrls) {
      const tileset: TilesetData = await loadTilesetJSON(url, nextId);
      nextId += tileset.tiles.length;
      this.terrainTilesets.push(tileset);
    }

    for (const url of featureUrls) {
      const tileset: TilesetData = await loadTilesetJSON(url, nextId);
      nextId += tileset.tiles.length;
      this.featureTilests.push(tileset);
    }

    return [...this.terrainTilesets, ...this.featureTilests];
  }

  getAllTileSets(): TilesetData[] {
    return [...this.terrainTilesets, ...this.featureTilests];
  }

  getTerrainTiles(): TileData[] {
    return this.terrainTilesets.flatMap(ts => ts.tiles);
  }

  getFeatureTiles(): TileData[] {
    return this.featureTilests.flatMap(ts => ts.tiles);
  }

  /**
   * Get a terrain tile for the given coordinates
   * Returns the tile ID (GID in tilemap terms, which is tile.id + 1)
   */
  selectTerrainTile(
    terrain: string,
    x: number,
    y: number,
    rivers: HexEdge,
    roads: HexEdge,
    mapSeed: string
  ): number | null {
    const key = `terrain:${terrain}:${x}:${y}:${mapSeed}`;

    // Check if already selected
    if (this.selections.has(key)) {
      return this.selections.get(key)!;
    }

    // Find candidates
    const candidates = this.getTerrainTiles().filter(t =>
      t.terrain === terrain &&
      t.rivers === rivers &&
      t.roads === roads
    );

    if (candidates.length === 0) {
      console.warn(`No terrain tiles found in tileset for: ${terrain} (rivers: [${edgesToStr(rivers)}], roads: [${edgesToStr(roads)}])`);
      return null;
    }

    // Select weighted random
    const selected = this.selectWeightedTile(candidates, x, y, mapSeed);
    if (selected) {
      const gid = selected.id;
      this.selections.set(key, gid);
      return gid;
    }

    return null;
  }

  /**
   * Get a feature tile for the given coordinates
   * Returns an object with the tile GID and whether it's terrain-specific
   */
  selectFeatureTile(
    feature: FeatureType,
    terrain: TerrainType,
    x: number,
    y: number,
    mapSeed: string
  ): { gid: number | null; isTerrainSpecific: boolean } {
    const key = `feature:${feature}:${terrain || 'none'}:${x}:${y}:${mapSeed}`;
    const featureTiles: TileData[] = this.getFeatureTiles();

    // Check if already selected
    if (this.selections.has(key)) {
      const gid = this.selections.get(key)!;
      const tile = featureTiles.find(t => t.id + 1 === gid);
      const isTerrainSpecific = !!(tile?.terrain && tile?.feature);
      return { gid, isTerrainSpecific };
    }

    // First try to find terrain-specific tiles if terrain is provided
    let candidates: TileData[] = [];
    let isTerrainSpecific = false;

    if (terrain) {
      candidates = featureTiles.filter(
        t => t.feature === feature && t.terrain === terrain
      );
      if (candidates.length > 0 && terrain != TerrainType.NONE) {
        isTerrainSpecific = true;
      }
    }

    // If no terrain-specific tiles, fall back to generic feature tiles
    if (candidates.length === 0) {
      candidates = featureTiles.filter(
        t => t.feature === feature && t.terrain === TerrainType.NONE
      );
    }

    if (candidates.length === 0) {
      console.warn(`No feature tiles found for: ${feature} (terrain: ${terrain || 'none'})`);
      return { gid: null, isTerrainSpecific: false };
    }

    // Select weighted random
    const selected = this.selectWeightedTile(candidates, x, y, mapSeed);
    if (selected) {
      const gid = selected.id;
      this.selections.set(key, gid);
      return { gid, isTerrainSpecific };
    }

    return { gid: null, isTerrainSpecific: false };
  }

  /**
   * Select a tile using weighted random selection with consistent seeding
   */
  private selectWeightedTile(
    candidates: TileData[],
    x: number,
    y: number,
    mapSeed: string
  ): TileData | null {
    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];

    // Create consistent seed from coordinates and map seed
    const seedStr = `${mapSeed}-${x}-${y}`;
    const rng = createRngFromStr(seedStr);

    // Calculate total weight
    const totalWeight = candidates.reduce((sum, tile) => sum + tile.weight, 0);

    // Use seeded random to pick a value
    const random = rng.next();
    let threshold = random * totalWeight;

    // Select tile based on weighted probability
    for (const tile of candidates) {
      threshold -= tile.weight;
      if (threshold <= 0) {
        return tile;
      }
    }

    // Fallback to last tile
    return candidates[candidates.length - 1];
  }

  /**
   * Clear all stored selections
   */
  clearSelections(): void {
    this.selections.clear();
  }
}
